import i18n, { currentLocale } from '../../../../i18n';
import { dateLocale } from '../../../../lib/localePrefs';
import type { HorizonMetric } from './horizonTypes';

function uiDateLocale(): string {
  return dateLocale(currentLocale());
}

export function metricAvailable(metric?: HorizonMetric | null): boolean {
  return !!metric && metric.available && metric.value != null;
}

export function formatHorizonNumber(value: number, unit?: string): string {
  if (unit === 'pct') {
    return `${value.toLocaleString('de-CH', { maximumFractionDigits: 0 })}%`;
  }
  return value.toLocaleString('de-CH', {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 1,
  });
}

/** Render a counted or modeled metric. Unavailable is a reason, never 0. */
export function formatHorizonMetric(
  metric?: HorizonMetric | null,
  empty = '—',
): string {
  if (!metric || !metricAvailable(metric)) return empty;
  return formatHorizonNumber(metric.value as number, metric.unit);
}

export function unavailableReason(metric?: HorizonMetric | null): string | null {
  if (!metric || metricAvailable(metric)) return null;
  return metric.unavailableReason || i18n.t('horizon:unavailable');
}

export function formatServiceDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.toLocaleDateString(uiDateLocale(), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/** Calendar day number, without the padding zero. */
export function dayOfMonth(iso: string): string {
  return String(Number(iso.slice(8, 10)) || '');
}

/** Booking time in the outlet's own timezone, so 19:00 means 19:00 there. */
export function formatClockInZone(ms: number, timeZone?: string | null): string {
  const parsed = new Date(ms);
  if (Number.isNaN(parsed.getTime())) return '';
  try {
    return parsed.toLocaleTimeString(uiDateLocale(), {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timeZone || undefined,
    });
  } catch {
    return parsed.toLocaleTimeString(uiDateLocale(), { hour: '2-digit', minute: '2-digit' });
  }
}

/** Weekday only, for the week agenda column heads. */
export function weekdayOf(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(uiDateLocale(), {
    weekday: 'short',
    timeZone: 'UTC',
  });
}

/**
 * Full weekday, for prose. "The last 4 Fris" is not English; anywhere the
 * weekday is spoken in a sentence needs the whole word.
 */
export function weekdayLongOf(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(uiDateLocale(), {
    weekday: 'long',
    timeZone: 'UTC',
  });
}

/** Day and month without the weekday, e.g. "22 Aug". */
export function dayAndMonth(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(uiDateLocale(), {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/** Short month name in the UI locale, e.g. "Sep" / "Sept.". */
export function monthShort(iso: string): string {
  const [y, m] = iso.split('-').map(Number);
  if (!y || !m) return '';
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(uiDateLocale(), {
    month: 'short',
    timeZone: 'UTC',
  });
}

/** Clock-only read time, for the "as of" line. */
export function formatReadTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleTimeString(uiDateLocale(), { hour: '2-digit', minute: '2-digit' });
}
